import React, { useState, useRef, useEffect, useMemo } from 'react';
import '../../style.css';
import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as GreenDot } from '../../../../assets/images/greendot.svg';
import { ReactComponent as RedDot } from '../../../../assets/images/reddot.svg';
import { ReactComponent as GreyDot } from '../../../../assets/images/greydot.svg';
import { ReactComponent as Dropdown } from '../../../../assets/images/dropdown-arrow.svg';
import SearchInput from '../../../common/SearchInputField';
import { ListCoRoleName } from '../../../../store/features/ListCoUserByRole';
import { ReactComponent as EmptyUserIcon } from '../../../../assets/images/emptyUserIcon.svg';
import { deleteUserApi } from '../../../../store/features/deleteCoUser';
import DeletePopUp from '../../DeletePopUp';
import { suspendUserApi } from '../../../../store/features/suspendCoUser';
import TeamSideBar from '../TeamSideBar';
import { BASE_URL } from '../../../../api/ApiConfig';
import { createAppSocket } from '../../../../api/createAppSocket';
import TeamManagementSkeleton from '../TeamManagementSkeleton';
import { coUserUpdateStatus } from '../../../../store/features/coUserUpdateStatus';
import { logoutAgent } from '../../../../store/features/logoutAgent';
import { logoutAll } from '../../../../store/features/LogoutAll';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { selectActiveChatLimit, setActiveChatLimit } from '../../../../Redux/teamSlice';
import {
  getTeamMgmtPeek,
  setTeamMgmtPeek,
  TEAM_MGMT_CACHE_TABS,
} from '../../../../utils/teamManagementViewCache';

const ENDPOINT = `${BASE_URL}`;

const chracterPosition = (alphabet) => {
  const alphabets = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const positionOfAlphabet = alphabets.indexOf(alphabet) + 1;
  return positionOfAlphabet;
};

const colorCodesForAvatar = [
  '#405261', '#FF4D4F', '#FA541C', '#FA8C16', '#FAAD14', '#EB2F96',
  '#FADB14', '#A0D911', '#52C41A', '#5CDBD3', '#9254DE', '#F759AB',
  '#2F54EB', '#722ED1', '#1890FF', '#13C2C2', '#2DB7F5', '#19BE6B',
  '#30A745', '#FA8C16', '#FAAD14', '#9254DE', '#F759AB', '#13C2C2',
  '#722ED1', '#2DB7F5',
];

function buildAgentRowsFromApiData(data) {
  const outputarray = [];
  if (!Array.isArray(data) || data.length === 0) return outputarray;
  data.forEach((item) => {
    const userAvatars = item.groups.map((user) => {
      const firstLetter = user.title.charAt(0).toUpperCase();
      return user.group_image ? (
        <Avatar key={user.id} src={user.group_image} />
      ) : (
        <Avatar
          key={user.id}
          sx={{
            bgcolor:
              colorCodesForAvatar[
                chracterPosition((user.title?.[0] ?? '').toUpperCase())
              ],
          }}
        >
          {firstLetter}
        </Avatar>
      );
    });

    outputarray.push({
      id: item._id,
      img: item.agent_image,
      name: item.full_name,
      email: item.email,
      role: item.role_name,
      group: userAvatars,
      isJoined: item.is_joined,
      isOffline: item.is_offline,
      accept_chat: item.accept_chat ?? true,
      ActiveUser: item.is_joined
        ? (item.accept_chat ? 'Accepting Chat' : 'Not Accepting Chat')
        : 'Invitation sent',
      DotColor: item.is_joined
        ? (item.accept_chat ? <GreenDot /> : <RedDot />)
        : <GreyDot />,
    });
  });
  return outputarray;
}

function AgentMain(props) {
  const userData = useSelector((state) => state.loginToken);
  const businessId = userData?.response?.user?.business_id;
  const agentsPeek = getTeamMgmtPeek(businessId, TEAM_MGMT_CACHE_TABS.AGENTS);
  const initialAgentRows =
    agentsPeek?.rawData !== undefined
      ? buildAgentRowsFromApiData(agentsPeek.rawData)
      : [];

  const [data, setData] = useState(initialAgentRows);
  const [statusMenuPosition, setStatusMenuPosition] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredData, setFilteredData] = useState(initialAgentRows);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [activeRowId, setActiveRowId] = useState(null);
  const [activeSidebar, setActiveSideBar] = useState(false);
  const [getUserList, setGetUserList] = useState([]);
  const [deleteUser, setDeleteUser] = useState(false);
  const [teamUserId, setTeamUserId] = useState('');
  const [isLoading, setIsLoading] = useState(agentsPeek?.rawData === undefined);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const activeChatLimit = useSelector(selectActiveChatLimit);
  const toolbarRef = useRef(null);
  const socketRef = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const uID = userData.response?.user?._id;
  const uRole = userData.response?.user?.role_name;

  const currentLanguage = useSelector((state) => state.language.currentLanguage);
  const t = useSelector((state) => state.language.translations[currentLanguage]);

  const { setChatLimitDetail, setAgentsCountLength, onViewAllGroups } = props;

  const [toolbarHeight, setToolbarHeight] = useState(0);

  const capitalize = (str, lower = false) =>
    (lower ? str.toLowerCase() : str).replace(/(?:^|\s|["'([{])+\S/g, (match) => match.toUpperCase());

  // ---------- Socket ----------
  useEffect(() => {
    socketRef.current = createAppSocket();
    socketRef.current.on('get-status', handleUpdateChatStatus);
    socketRef.current.on('check-login', handleLogin);
    socketRef.current.on('check-single-logout', handleLogout);
    socketRef.current.on('check-logout', handleLogout);

    return () => {
      socketRef.current.off('get-status', handleUpdateChatStatus);
      socketRef.current.off('check-login', handleLogin);
      socketRef.current.off('check-single-logout', handleLogout);
      socketRef.current.off('check-logout', handleLogout);
    };
  }, []);

  // ---------- Toolbar height ----------
  useEffect(() => {
    if (toolbarRef.current) {
      setToolbarHeight(toolbarRef.current.offsetHeight);
    }
  }, [filteredData.length]);

  const tableContainerHeight = useMemo(() => {
    return `calc(100vh - ${350 + toolbarHeight}px)`;
  }, [toolbarHeight]);

  // ---------- Socket handlers ----------
  const handleLogout = (agentId) => {
    setFilteredData((prev) =>
      prev.map((item) =>
        item.id === agentId
          ? { ...item, isOffline: true, ActiveUser: 'Offline', DotColor: <GreyDot /> }
          : item
      )
    );
  };

  const handleLogin = (agentId) => {
    setFilteredData((prev) =>
      prev.map((item) =>
        item.id === agentId
          ? { ...item, isOffline: false, ActiveUser: 'Accepting Chat', DotColor: <GreenDot /> }
          : item
      )
    );
  };

  const handleUpdateChatStatus = (agentId, status) => {
    setFilteredData((prev) =>
      prev.map((item) =>
        item.id === agentId
          ? {
            ...item,
            ActiveUser: status ? 'Accepting Chat' : 'Not Accepting Chat',
            DotColor: status ? <GreenDot /> : <RedDot />,
          }
          : item
      )
    );
  };

  // ---------- Load agents ----------
  const RoleCoUserByID = async () => {
    const listCoUserList = dispatch(ListCoRoleName({ name: 'agent' }));

    listCoUserList
      .then(async (payload) => {
        if (!payload?.payload) {
          toast.error('Some Error Occurred. Please try again later.');
          setIsLoading(false);
          return;
        }

        const { status, data, message } = payload.payload;
        const list = Array.isArray(data) ? data : [];

        if (status === 200) {
          if (businessId) {
            setTeamMgmtPeek(businessId, TEAM_MGMT_CACHE_TABS.AGENTS, list);
          }
          const outputarray = buildAgentRowsFromApiData(list);
          setData(outputarray);
          setFilteredData(outputarray);
          setAgentsCountLength?.(outputarray.length);
        } else {
          setFilteredData([]);
          setData([]);
          toast.error(message ?? 'Failed to load agents');
        }
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        toast.error(err.message);
      });
  };

  useEffect(() => {
    RoleCoUserByID();
  }, []);

  // ---------- Search ----------
  const handleSearchInputChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setPage(0);
    const filtered = data.filter(
      (row) =>
        row.name.toLowerCase().includes(query) ||
        row.email.toLowerCase().includes(query)
    );
    setFilteredData(filtered);
  };

  // ---------- Pagination ----------
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };
  const visibleRows = useMemo(
    () => filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredData, page, rowsPerPage]
  );

  // ---------- Row click ----------
  const handleRowClick = (row) => {
    setActiveSideBar(row.id);
    setActiveRowId(row.id);
    setGetUserList(row);
  };

  // ---------- Action menu ----------
  const handleMenuClick = (e, row) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedUser(row);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  // ---------- Status dropdown ----------
  const handleStatusMenuClick = (event, row) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();

    setStatusMenuPosition({
      top: rect.bottom + window.scrollY, // Position below the clicked element
      left: rect.left + window.scrollX,  // Align with the left edge of clicked element
    });
    setSelectedUser(row);
  };
  const handleStatusMenuClose = () => {
    setStatusMenuPosition(null);
    setSelectedUser(null);
  };

  // ---------- Update accept_chat ----------
  const chatStatusUpdate = async (agentId, status) => {
    const res = dispatch(
      coUserUpdateStatus({
        id: agentId,
        requestHeaders: { accept_chat: status },
      })
    );

    res.then((data) => {
      if (data?.payload?.status === 200) {
        RoleCoUserByID(); // refresh list
        socketRef.current.emit('update-status', agentId, status);
        handleStatusMenuClose();
      }
    }).catch((err) => toast.error(err.message));
  };

  // ---------- Logout ----------
  const logoutUser = async (agentId) => {
    const res = dispatch(
      logoutAgent({
        requestHeaders: { agentId, adminId: uID },
      })
    );

    res.then((data) => {
      socketRef.current.emit('logout-agent', agentId);
      if (agentId === uID) dispatch(logoutAll());
      if (data?.payload?.status === 200) {
        RoleCoUserByID();
        handleStatusMenuClose();
      }
    }).catch((err) => toast.error(err.message));
  };

  // ---------- Suspend / Delete ----------
  const suspendUserHandler = async (rowID) => {
    const res = dispatch(suspendUserApi({ requestHeaders: { id: rowID } }));
    res.then((data) => {
      if (data?.payload?.success) {
        toast.success(data.payload.message);
        RoleCoUserByID();
        props.onSuspenseChange?.();
        socketRef.current.emit('logout-agent', rowID);
        handleMenuClose();
      }
    }).catch((err) => toast.error(err.message));
  };

  const deleteUserHandler = async () => {
    const res = dispatch(deleteUserApi({ requestHeaders: { id: teamUserId } }));
    res.then((data) => {
      if (data?.payload?.status === 200) {
        toast.success(data.payload.message);
        setAgentsCountLength((prev) => prev - 1);
        RoleCoUserByID();
      }
    }).catch((err) => toast.error(err.message));
  };

  // ---------- Counts ----------
  const activeCount = filteredData.filter((r) => r.isJoined).length;

  if (isLoading) return <TeamManagementSkeleton />;

  const headCells = [
    { id: 'name', label: t.NAMETB },
    { id: 'email', label: t.EMAILTB },
    { id: 'groups', label: t.GROUPS },
    { id: 'status', label: t.STATUSTB },
    { id: 'actions', label: '' },
  ];

  return (
    <>
      <div ref={toolbarRef} className="team-container">
        <SearchInput
          placeholder="Search"
          className="SearchInputField-teamsection"
          value={searchQuery}
          onChange={handleSearchInputChange}
        />
      </div>

      <div className="Team-Section-Content-Container">
        {filteredData.length === 0 ? (
          <div className="NoSuspendedAgent-main-Conatiner">
            <div className="TeamChatBot-Main-Container">
              <EmptyUserIcon />
              <p className="Chatbot-devploment">{t.NOAVAILABLEUSERS}</p>
              <p className="Chatbot-crafting">{t.NOAVAILABLEUSERSMSG}</p>
            </div>
          </div>
        ) : (
          <>
            <p className="Team-total-active-user">Active ({activeCount})</p>

            <TableContainer
              sx={{
                boxShadow: 'none',
                flex: 1,
                marginTop: '2px',
                marginBottom: '40px',
                height: tableContainerHeight,
                overflowY: 'auto',
              }}
            >
              <Table stickyHeader sx={{ minWidth: 750 }}>
                <TableHead>
                  <TableRow>
                    {headCells.map((h, i) => (
                      <TableCell
                        key={h.id}
                        sx={{
                          paddingLeft: i === 0 ? 0 : '16px',
                          paddingBottom: '9px !important',
                          paddingTop: 0,
                          zIndex: activeChatLimit
                            ? 'unset'
                            : (!activeSidebar && !deleteUser ? 7 : 1),
                          backgroundColor: '#fff',
                        }}
                      >
                        <Typography
                          sx={{
                            margin: 0,
                            fontSize: '12px',
                            fontWeight: 400,
                            color: '#405261',
                            fontFamily: 'Inter',
                          }}
                        >
                          {h.label}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {visibleRows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      onClick={() => handleRowClick(row)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#F9FAFA' },
                        '& td': {
                          height: '4rem',
                          py: 0,
                          verticalAlign: 'middle',
                          color: '#405261',
                          fontFamily: 'Inter',
                          fontSize: '14px',
                          fontWeight: 400,
                        },
                      }}
                    >
                      {/* Name + Avatar */}
                      <TableCell sx={{ paddingLeft: 0 }}>
                        <div className="team-table-img">
                          {row.img ? (
                            <Avatar src={row.img} sx={{ width: 32, height: 32 }} />
                          ) : (
                            <Avatar
                              sx={{
                                width: 32,
                                height: 32,
                                fontSize: '16px',
                                bgcolor:
                                  colorCodesForAvatar[
                                  chracterPosition((row.name?.[0] ?? '').toUpperCase())
                                  ],
                              }}
                            >
                              {(row.name?.[0] ?? '').toUpperCase()}
                            </Avatar>
                          )}
                          <p className="team-table-rowtext">{capitalize(row.name)}</p>
                        </div>
                      </TableCell>

                      {/* Email */}
                      <TableCell>{row.email}</TableCell>

                      {/* Groups */}
                      <TableCell>
                        <AvatarGroup

                          max={4}
                          sx={{
                            justifyContent: 'flex-start',

                            WebkitJustifyContent: 'left',
                            '& .MuiAvatar-root': { width: 32, height: 32 },
                            '& .MuiAvatar-root:nth-of-type(2)': {
                              zIndex: activeChatLimit ? 'unset' : (!activeSidebar ? 2 : 1),
                              marginLeft: '-8px',
                            },
                            '& .MuiAvatar-root:nth-of-type(3)': { zIndex: 1, marginLeft: '-8px' },
                            '& .MuiAvatar-root:nth-of-type(4)': { zIndex: 0, marginLeft: '-8px' },
                            '& .MuiAvatarGroup-avatar': {
                              zIndex: activeChatLimit ? 'unset' : (!activeSidebar ? 5 : 1),
                              fontFamily: 'Inter',
                              fontSize: '14px',
                              fontWeight: 500,
                              lineHeight: '18px',
                              border: 'none',
                              width: 32,
                              height: 32,
                            },
                          }}
                        >
                          {row.group.map((av, i) => (
                            <Avatar key={i} src={av.props.src} sx={av.props.sx}>
                              {av.props.children}
                            </Avatar>
                          ))}
                        </AvatarGroup>
                      </TableCell>

                      {/* Status – now with dropdown */}
                      <TableCell
                        sx={{
                          color: '#061522',
                          fontWeight: 500,
                          '& svg': { display: 'inline-block', verticalAlign: 'middle' },
                        }}
                      >
                        {row.isJoined && !row.isOffline ? (
                          <div
                            className="team-activeuser"
                            onClick={(e) => {
                              if (uRole === 'admin' || uRole === 'admin_with_billing' || row.id === uID) {
                                handleStatusMenuClick(e, row);
                              }
                            }}
                            style={{
                              cursor:
                                uRole === 'admin' || uRole === 'admin_with_billing' || row.id === uID
                                  ? 'pointer'
                                  : 'default',
                            }}
                          >
                            {row.DotColor}
                            {row.ActiveUser}
                            {(uRole === 'admin' || uRole === 'admin_with_billing' || row.id === uID) && (
                              <Dropdown style={{ marginLeft: 4 }} />
                            )}
                          </div>
                        ) : row.isOffline ? (
                          <div className="team-activeuser">
                            <GreyDot /> Offline
                          </div>
                        ) : (
                          <div className="team-invitation-sent">Invitation sent</div>
                        )}
                      </TableCell>

                      {/* Actions */}
                      <TableCell padding="none">
                        {(uRole === 'admin' || uRole === 'admin_with_billing' || row.id === uID) && (
                          <IconButton
                            onClick={(e) => handleMenuClick(e, row)}
                            size="small"
                            sx={{ ml: 1, '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' } }}
                          >
                            <MoreHorizIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {!activeChatLimit && !activeSidebar && (
              <Box
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  left: 0,
                  width: '100%',
                  background: '#fff',
                  zIndex: 10,
                  padding: '16px 24px',
                  borderTop: '1px solid #E5E9EC',
                }}
              >
                <TablePagination
                  component="div"
                  count={filteredData.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[]}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelDisplayedRows={({ from, to }) => (
                    <Typography
                      sx={{
                        color: '#405261',
                        fontFamily: 'Inter',
                        fontSize: '14px',
                        fontWeight: 400,
                        lineHeight: '18px',
                      }}
                    >
                      Agents {from}-{to}
                    </Typography>
                  )}
                  ActionsComponent={(props) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                      <Button
                        onClick={(e) => props.onPageChange(e, props.page - 1)}
                        disabled={props.page === 0}
                        sx={{
                          width: '128px',
                          height: '40px',
                          borderRadius: '8px',
                          border: '1px solid #D5DCE1',
                          background: '#FFF',
                          boxShadow: '0px 1px 2px 0px rgba(6,21,34,0.05)',
                          color: '#405261',
                          fontSize: '14px',
                          fontWeight: 500,
                          textTransform: 'none',
                          '&:not(.Mui-disabled):hover': {
                            backgroundColor: '#FFF',
                            borderColor: '#C5CED6',
                          },
                          '&.Mui-disabled': {
                            color: '#93A1B0',
                            border: '1px solid #C5CED6',
                            bgcolor: '#F9FAFA',
                          },
                        }}
                      >
                        Previous
                      </Button>
                      <Button
                        onClick={(e) => props.onPageChange(e, props.page + 1)}
                        disabled={props.page >= Math.ceil(props.count / rowsPerPage) - 1}
                        sx={{
                          width: '88px',
                          height: '40px',
                          borderRadius: '8px',
                          border: '1px solid #D5DCE1',
                          background: '#FFF',
                          boxShadow: '0px 1px 2px 0px rgba(6,21,34,0.05)',
                          color: '#405261',
                          fontSize: '14px',
                          fontWeight: 500,
                          textTransform: 'none',
                          '&:not(.Mui-disabled):hover': {
                            backgroundColor: '#FFF',
                            borderColor: '#C5CED6',
                          },
                          '&.Mui-disabled': {
                            color: '#93A1B0',
                            border: '1px solid #C5CED6',
                            bgcolor: '#F9FAFA',
                          },
                        }}
                      >
                        Next
                      </Button>
                    </Box>
                  )}
                  sx={{
                    '.MuiTablePagination-toolbar': {
                      padding: 0,
                      minHeight: 'unset',
                      display: 'flex',
                      justifyContent: 'space-between',
                    },
                    '.MuiTablePagination-spacer': { display: 'none' },
                    '.MuiTablePagination-selectLabel': { display: 'none' },
                    '.MuiTablePagination-select': { display: 'none' },
                    '.MuiTablePagination-selectIcon': { display: 'none' },
                  }}
                />
              </Box>
            )}
          </>
        )}

        {/* ---------- Status Menu ---------- */}
        <Menu
          open={Boolean(statusMenuPosition)}
          onClose={handleStatusMenuClose}
          anchorReference="anchorPosition"
          anchorPosition={statusMenuPosition}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          PaperProps={{
            elevation: 0,
            sx: {
              filter: 'none',
              color: '#405261',
              mt: 1.5,
              width: 200,
              px: '8px',
              borderRadius: '8px',
              border: '1px solid #D5DCE1',
              boxShadow: '0 15px 20px 0 rgba(6,21,34,0.05)',
              '& .MuiMenuItem-root': {
                px: '8px',
                py: 1,
                fontSize: '14px',
                fontFamily: 'Inter',
                color: '#405261',
                '&:hover': { color: '#061522', borderRadius: '8px', background: '#F9FAFA !important' },
              },
            },
          }}
        >
          <MenuItem onClick={() => chatStatusUpdate(selectedUser?.id, true)}>
            <GreenDot style={{ marginRight: 8 }} />
            {t.ACCEPTCHAT}
          </MenuItem>
          <MenuItem onClick={() => chatStatusUpdate(selectedUser?.id, false)}>
            <RedDot style={{ marginRight: 8 }} />
            {t.NOTACCEPT}
          </MenuItem>
          <MenuItem onClick={() => logoutUser(selectedUser?.id)}>
            <GreyDot style={{ marginRight: 8 }} />
            {t.LOGOUT}
          </MenuItem>
        </Menu>

        {/* ---------- Actions Menu ---------- */}

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            elevation: 0,
            sx: {
              filter: "none",
              color: '#405261',
              mt: '-5px',
              width: 200,
              paddingX: "8px",
              borderRadius: "8px", // ✅ your custom border radius
              border: "1px solid #D5DCE1", // ✅ your custom border

              boxShadow: "0 15px 20px 0 rgba(6, 21, 34, 0.05)", // ✅ soft shadow
              "& .MuiMenuItem-root": {
                px: "8px",
                py: 1,
                fontSize: '14px',
                fontFamily: 'Inter',
                color: '#405261',

                "&:hover": { color: "#061522", borderRadius: "8px", background: "#F9FAFA !important" },
              },
            },
          }}
        >
          <MenuItem
            onClick={() => {
              handleMenuClose();
              navigate(`/userupdate/${selectedUser?.id}`);
            }}
          >
            {t.EDITPROFILE}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setChatLimitDetail({
                id: selectedUser?.id,
                name: selectedUser?.name,
                email: selectedUser?.email,
                img: selectedUser?.img,
              });
              dispatch(setActiveChatLimit(true));
              handleMenuClose();
            }}
          >
            {t.CHANGECHAT}
          </MenuItem>
          <MenuItem onClick={handleMenuClose}>{t.VIEWAGENT}</MenuItem>
          {(uRole === 'admin' || uRole === 'admin_with_billing') && (
            <>
              <MenuItem onClick={() => suspendUserHandler(selectedUser?.id)}>{t.SUSPENSEAGENT}</MenuItem>
              <MenuItem
                sx={{
                  color: '#FF4D4F !important',
                  "&:hover": {
                    color: '#FF4D4F !important', // ✅ stays red no matter what

                    borderRadius: '8px',
                  },
                }}
                onClick={() => {
                  setTeamUserId(selectedUser?.id);
                  setDeleteUser(true);
                  handleMenuClose();
                }}
              >
                {t.DELETEALL}
              </MenuItem>
            </>
          )}
        </Menu>

        {/* ---------- Delete Popup ---------- */}
        {deleteUser && (
          <DeletePopUp
            onCancel={() => setDeleteUser(false)}
            onDelete={() => {
              deleteUserHandler();
              setDeleteUser(false);
            }}
          />
        )}

        {/* ---------- Sidebar ---------- */}
        {activeSidebar && (
          <>
            <div className="overlay" />
            <TeamSideBar
              onViewAllGroups={onViewAllGroups}
              onCancel={() => setActiveSideBar(false)}
              onOpen={() => setActiveSideBar(true)}
              userList={getUserList}
            />
          </>
        )}
      </div>
    </>
  );
}

export default AgentMain;